/**
 * Fetch wrapper that preserves HTTP status on errors.
 *
 * TanStack Query's retry function needs the status code to skip 429 retries.
 * Raw `throw new Error("...")` loses the status — this wrapper attaches it.
 *
 * Also provides a global rate-limit circuit breaker: the first 429 sets a
 * module-level timestamp and all subsequent fetches short-circuit with a
 * synthetic 429 until the window expires. Browser JS is single-threaded so
 * no mutex is needed.
 *
 * @module lib/api/fetchWithStatus
 */

/** Error class that preserves HTTP status for TanStack Query retry logic */
export class FetchError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: Record<string, unknown>,
    /** Machine-readable error code from the API envelope, when present. */
    public code?: string,
  ) {
    super(message);
    this.name = "FetchError";
  }
}

/**
 * Build a FetchError from a failed Response, tolerating both API error
 * envelopes in the codebase: the flat `{ error: string, code }` shape most
 * Next API routes return, and the backend middleware's nested
 * `{ error: { message, code } }` shape.
 *
 * Exists because call sites that hand-read `err.error` / `err.code` off ONE
 * shape silently break on the other: the caller-ID wizard surfaced months of
 * generic "Something went wrong" (and "[object Object]" on the TFV page)
 * while the backend was sending crafted, actionable messages — users read
 * that as "the button does nothing" and rage-clicked (PostHog, Aug 2026).
 */
export async function fetchErrorFromResponse(
  res: Response,
  fallback: string,
): Promise<FetchError> {
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  const errField = body.error;
  const nested =
    typeof errField === "object" && errField !== null
      ? (errField as Record<string, unknown>)
      : null;
  const message =
    (typeof errField === "string" && errField) ||
    (typeof nested?.message === "string" && (nested.message as string)) ||
    fallback;
  const code =
    (typeof body.code === "string" && (body.code as string)) ||
    (typeof nested?.code === "string" && (nested.code as string)) ||
    undefined;
  return new FetchError(message, res.status, body, code);
}

// ---------------------------------------------------------------------------
// Global rate-limit circuit breaker
// ---------------------------------------------------------------------------

/** Epoch ms until which all fetches should short-circuit with 429. */
let rateLimitedUntil = 0;

let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
// Additive registry — every subscriber fires on recovery. A single slot
// silently last-writer-wins: DialerExecution's targeted recover invalidate
// used to overwrite providers' global one (inbox 429 self-trip, Tier 2).
const onRecoverCallbacks = new Set<() => void>();

/** Parse Retry-After header — supports both seconds and HTTP-date formats. */
function parseRetryAfter(header: string | null): number {
  if (!header) return 60;
  const seconds = parseInt(header, 10);
  if (!isNaN(seconds) && seconds > 0) return Math.min(seconds, 300);
  // HTTP-date format fallback (RFC 7231 §7.1.1.1)
  const date = Date.parse(header);
  if (!isNaN(date)) return Math.min(Math.max((date - Date.now()) / 1000, 1), 300);
  return 60;
}

/**
 * Throws FetchError(429) if the circuit breaker is active.
 * Call before every fetch to short-circuit without network activity.
 */
export function checkRateLimitGuard(): void {
  if (Date.now() < rateLimitedUntil) {
    throw new FetchError("Rate limited (circuit breaker)", 429);
  }
}

/**
 * Whether the circuit breaker is currently active. Read from UI to render
 * a "Reconnecting…" banner / keep idempotent actions clickable while
 * cache is stale. Bug C fix (2026-05-25, "Dialer UI bug" incident):
 * customers saw a zombie LIVE state when 429s tripped the breaker; the
 * UI had no signal to degrade gracefully. Now it does.
 */
export function isRateLimited(): boolean {
  return Date.now() < rateLimitedUntil;
}

/**
 * Milliseconds until the breaker auto-recovers. Returns 0 if not active.
 * Useful for "Reconnecting in Ns…" countdowns.
 */
export function rateLimitedFor(): number {
  return Math.max(0, rateLimitedUntil - Date.now());
}

/**
 * Activate the circuit breaker. Call when any fetch receives a 429.
 * Parses the Retry-After header to determine the cooldown window.
 */
export function onRateLimited(retryAfterHeader: string | null): void {
  const waitSec = parseRetryAfter(retryAfterHeader);
  const waitMs = waitSec * 1000;
  rateLimitedUntil = Date.now() + waitMs;

  // Schedule auto-recovery (clear old timer if breaker extended)
  if (recoveryTimer) clearTimeout(recoveryTimer);
  recoveryTimer = setTimeout(() => {
    recoveryTimer = null;
    // Snapshot so a callback that (un)subscribes during iteration is safe.
    // Isolate each: one throwing subscriber must not block the others (e.g.
    // providers' invalidate must not prevent the dialer's recovery).
    for (const cb of [...onRecoverCallbacks]) {
      try {
        cb();
      } catch {
        // Swallow — a recovery callback throwing is non-fatal to the others.
      }
    }
  }, waitMs);
}

/**
 * Register a callback to fire when the circuit breaker window expires.
 * Returns an unsubscribe — callers in effects MUST call it on cleanup so
 * remounts don't accumulate duplicate callbacks.
 */
export function onCircuitBreakerRecover(cb: () => void): () => void {
  onRecoverCallbacks.add(cb);
  return () => {
    onRecoverCallbacks.delete(cb);
  };
}

// ---------------------------------------------------------------------------
// fetchOk
// ---------------------------------------------------------------------------

/**
 * Fetch wrapper that throws FetchError with status on non-ok responses.
 * Drop-in replacement for `fetch()` in queryFn functions.
 *
 * Checks the global rate-limit circuit breaker before hitting the network.
 *
 * @example
 * ```ts
 * queryFn: async () => {
 *   const res = await fetchOk("/api/phone-numbers");
 *   const data = await res.json();
 *   return data.phoneNumbers;
 * }
 * ```
 */
export async function fetchOk(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  checkRateLimitGuard();

  const res = await fetch(input, init);
  if (!res.ok) {
    if (res.status === 429) {
      onRateLimited(res.headers.get("Retry-After"));
    }
    const body = await res.json().catch(() => undefined);
    throw new FetchError(
      body?.error || `Request failed: ${res.status} ${res.statusText}`,
      res.status,
      body,
    );
  }
  return res;
}
